import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiUser, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './Auth.css';
import logo from './logo_efc.svg';

const Login = () => {
  const [formData, setFormData] = useState({
    emailOrUsername: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.emailOrUsername, formData.password);

    if (result.success) {
      navigate('/');
    }

    setNeedsVerification(!!result.needsVerification);
    setNeedsApproval(!!result.needsApproval);
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const response = await authAPI.resendVerification(formData.emailOrUsername);
      toast.info(response.message || 'Bestätigungslink wurde erneut verschickt.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Der Bestätigungslink konnte nicht verschickt werden.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="container">
        <div className="auth-form">
          <div className="auth-header">
            <img src={logo} alt="EFC Buseck" className="auth-logo" />
            <h2>Willkommen zurück</h2>
            <p className="auth-subtitle">Melde dich in deinem EFC-Buseck-Konto an</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="emailOrUsername">E-Mail oder Benutzername</label>
              <div className="input-wrap">
                <FiUser className="input-icon" aria-hidden="true" />
                <input
                  type="text"
                  id="emailOrUsername"
                  name="emailOrUsername"
                  value={formData.emailOrUsername}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                  placeholder="deine.email@beispiel.de"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Passwort</label>
              <div className="input-wrap has-toggle">
                <FiLock className="input-icon" aria-hidden="true" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  placeholder="Dein Passwort"
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="auth-forgot">
              <Link to="/passwort-vergessen">Passwort vergessen?</Link>
            </div>

            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? 'Wird angemeldet …' : 'Anmelden'}
            </button>
          </form>

          {needsApproval && (
            <div className="auth-hint">
              <p>
                Deine E-Mail-Adresse ist bestätigt, dein Konto muss aber noch vom Vorstand
                freigeschaltet werden. Wir melden uns per E-Mail, sobald es soweit ist.
              </p>
            </div>
          )}

          {needsVerification && (
            <div className="auth-hint">
              <p>
                Deine E-Mail-Adresse ist noch nicht bestätigt. Ohne Bestätigung ist keine
                Anmeldung möglich. Schau auch im Spam-Ordner nach.
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleResendVerification}
                disabled={resending || !formData.emailOrUsername}
              >
                {resending ? 'Wird verschickt …' : 'Bestätigungslink erneut senden'}
              </button>
            </div>
          )}

          <div className="auth-links">
            <p>
              Noch kein Account?{' '}
              <Link to="/registrieren">Jetzt registrieren</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
